/**
 * Sanitizer utility - защита от XSS и удаление нежелательного контента
 *
 * Функции:
 * - Удаление всех ссылок (URLs и <a> тегов)
 * - Удаление опасных HTML тегов (script, iframe, object, embed)
 * - Удаление event handlers (onclick, onerror, etc.)
 * - Белый список безопасных тегов для форматирования
 */

// Белый список разрешенных тегов (только для форматирования)
const ALLOWED_TAGS = [
  'p', 'div', 'span', 'br',
  'table', 'thead', 'tbody', 'tr', 'td', 'th',
  'ul', 'ol', 'li',
  'b', 'strong', 'i', 'em', 'u'
];

// Белый список разрешенных атрибутов (минимальный набор)
const ALLOWED_ATTRIBUTES = [
  'colspan', 'rowspan'
];

/**
 * Удаляет все URL из текста (http://, https://, www.)
 */
export function removeUrls(text) {
  if (!text) return '';

  // Удаляем URL-адреса различных форматов
  let cleaned = text;

  // http:// и https://
  cleaned = cleaned.replace(/https?:\/\/[^\s<>"{}|\\^`\[\]]+/gi, '[ссылка удалена]');

  // www.
  cleaned = cleaned.replace(/www\.[^\s<>"{}|\\^`\[\]]+/gi, '[ссылка удалена]');

  // email-адреса (опционально)
  // cleaned = cleaned.replace(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g, '[email удален]');

  return cleaned;
}

/**
 * Удаляет опасные теги и атрибуты из HTML
 */
export function sanitizeHtml(html) {
  if (!html || typeof html !== 'string') return '';

  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Удаляем опасные элементы
    const dangerousTags = [
      'script', 'iframe', 'object', 'embed', 'link', 'style',
      'base', 'meta', 'applet', 'form', 'input', 'button',
      'textarea', 'select', 'option'
    ];

    dangerousTags.forEach(tag => {
      const elements = doc.querySelectorAll(tag);
      elements.forEach(el => el.remove());
    });

    // Удаляем все <a> теги (ссылки), заменяя на текст
    const links = doc.querySelectorAll('a');
    links.forEach(link => {
      if (link.parentNode) {
        const text = document.createTextNode(link.textContent || '[ссылка удалена]');
        link.parentNode.replaceChild(text, link);
      }
    });

    // Рекурсивная очистка элементов
    function cleanElement(element) {
      if (!element || !element.parentNode) return;

      // Удаляем все event handlers
      const attributes = Array.from(element.attributes || []);
      attributes.forEach(attr => {
        const attrName = attr.name.toLowerCase();

        // Удаляем event handlers (on*)
        if (attrName.startsWith('on')) {
          element.removeAttribute(attr.name);
        }

        // Удаляем опасные атрибуты
        if (['href', 'src', 'action', 'formaction', 'data', 'codebase'].includes(attrName)) {
          element.removeAttribute(attr.name);
        }

        // Оставляем только разрешенные атрибуты
        if (!ALLOWED_ATTRIBUTES.includes(attrName)) {
          element.removeAttribute(attr.name);
        }
      });

      // Проверяем, разрешен ли этот тег
      const tagName = element.tagName ? element.tagName.toLowerCase() : '';
      if (tagName && !ALLOWED_TAGS.includes(tagName)) {
        // Заменяем неразрешенный тег на его содержимое
        const parent = element.parentNode;
        if (parent) {
          while (element.firstChild) {
            parent.insertBefore(element.firstChild, element);
          }
          element.remove();
        }
        return;
      }

      // Рекурсивно обрабатываем дочерние элементы
      const children = Array.from(element.children || []);
      children.forEach(child => cleanElement(child));
    }

    // Очищаем содержимое body (не сам body!)
    if (doc.body) {
      // Очищаем дочерние элементы, а не сам body
      const children = Array.from(doc.body.children || []);
      children.forEach(child => cleanElement(child));
      return doc.body.innerHTML || '';
    }

    return '';
  } catch (error) {
    // В случае ошибки возвращаем plain text
    return htmlToPlainText(html);
  }
}

/**
 * Полная санитизация: удаление URL + очистка HTML
 */
export function fullSanitize(content) {
  if (!content) return '';

  // Сначала удаляем URL из исходного текста
  let cleaned = removeUrls(content);

  // Затем санитизируем HTML
  cleaned = sanitizeHtml(cleaned);

  return cleaned;
}

/**
 * Безопасная вставка текста в contentEditable
 */
export function safeInsertHtml(contentEditableElement, html) {
  if (!contentEditableElement || !html) return '';

  // Санитизируем HTML
  const cleanHtml = fullSanitize(html);

  // Вставляем очищенный HTML
  contentEditableElement.innerHTML = cleanHtml;

  return cleanHtml;
}

/**
 * Валидация текста заказа (проверка на подозрительный контент)
 */
export function validateOrderText(text) {
  if (!text || typeof text !== 'string') {
    return { valid: false, error: 'Текст заказа пустой' };
  }

  // Проверка минимальной длины
  if (text.trim().length < 3) {
    return { valid: false, error: 'Текст заказа слишком короткий' };
  }

  // Проверка максимальной длины (защита от DoS)
  if (text.length > 1000000) { // 1MB
    return { valid: false, error: 'Текст заказа слишком длинный (максимум 1MB)' };
  }

  // Проверка на подозрительные паттерны
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /onerror=/i,
    /onclick=/i,
    /<iframe/i,
    /eval\(/i,
    /expression\(/i
  ];

  for (const pattern of suspiciousPatterns) {
    if (pattern.test(text)) {
      return {
        valid: false,
        error: 'Обнаружен подозрительный контент. Пожалуйста, используйте только текст или таблицы.'
      };
    }
  }

  return { valid: true };
}

/**
 * Экспорт plain text из HTML (удаление всех тегов)
 */
export function htmlToPlainText(html) {
  if (!html) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  return doc.body.textContent || '';
}
